<template>
  <div class="user-tab-security">
    <v-card class="mb-7">
      <v-card-title> {{ $t('candidateData') }} </v-card-title>

      <v-card-text>
        <v-form ref="dataForm" v-model="valid" @submit.prevent="handleFormSubmit">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="candidateDataLocal.firstName"
                outlined
                dense
                maxlength="100"
                hide-details="auto"
                :label="$t('firstNameRequired')"
                :placeholder="$t('firstNameRequired')"
                :rules="[validators.required]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="candidateDataLocal.lastName"
                outlined
                dense
                maxlength="100"
                hide-details="auto"
                :label="$t('lastNameRequired')"
                :placeholder="$t('lastNameRequired')"
                :rules="[validators.required]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="candidateDataLocal.phone"
                outlined
                dense
                maxlength="31"
                hide-details="auto"
                :label="$t('phoneNumber')"
                :placeholder="$t('phoneNumber')"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="candidateDataLocal.preferredCity"
                outlined
                dense
                maxlength="128"
                hide-details="auto"
                :label="$t('preferredCity')"
                :placeholder="$t('preferredCity')"
              />
            </v-col>
          </v-row>

          <!-- Custom fields -->
          <v-row v-if="localCustomFields.length">
            <v-col cols="12">
              <v-divider class="mb-4" />
              <h3 class="text-lg font-weight-semibold mb-3">{{ $t('customFields') }}</h3>
            </v-col>
            <v-col
              v-for="(customField, index) in localCustomFields"
              :key="`CustomField-${customField.id}-${index}`"
              cols="12"
            >
              <custom-field-input :field="customField" />
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12">
              <v-btn
                color="primary"
                class="me-3"
                type="submit"
                :loading="loading"
                :disabled="!valid || !areDataChanged"
              >
                {{ $t('save') }}
              </v-btn>
              <v-btn outlined color="secondary" :disabled="!areDataChanged" @click="restoreDefaults">
                {{ $t('discard') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'
import { required } from '@core/utils/validation'
import _ from 'lodash'
import { getCustomFieldsForCandidate } from '@/utils/customFieldUtils'
import CustomFieldInput from '@/views/_shared/CustomFieldInput.vue'

export default {
  components: {
    CustomFieldInput,
  },
  setup(props, { emit }) {
    const { updateCandidateInOffer } = useActions('jobs', ['updateCandidateInOffer'])
    const { jobCandidateData } = useState('jobs', ['jobCandidateData'])

    const loading = ref(false)
    const valid = ref(true)
    const dataForm = ref(null)
    const localCustomFields = ref([])
    const candidateDataLocal = ref({})

    const syncLocalState = () => {
      candidateDataLocal.value = _.cloneDeep({
        firstName: jobCandidateData.value.candidate.user.firstName,
        lastName: jobCandidateData.value.candidate.user.lastName,
        phone: jobCandidateData.value.candidate.user.phone,
        preferredCity: jobCandidateData.value.preferredCity,
      })
      localCustomFields.value = _.cloneDeep(
        getCustomFieldsForCandidate(jobCandidateData.value.customFields || []),
      )
    }

    syncLocalState()

    watch(
      () => jobCandidateData.value,
      () => {
        syncLocalState()
      },
      { deep: true },
    )

    const handleFormSubmit = async () => {
      const isFormValid = dataForm.value.validate()
      if (!isFormValid) return

      loading.value = true
      const { success } = await updateCandidateInOffer({
        candidateDetailId: jobCandidateData.value.id,
        payload: {
          user: {
            firstName: candidateDataLocal.value.firstName,
            lastName: candidateDataLocal.value.lastName,
            phone: candidateDataLocal.value.phone,
          },
          preferredCity: candidateDataLocal.value.preferredCity,
          customFields: localCustomFields.value,
        },
      })
      loading.value = false

      if (success) {
        emit('refetchCandidate')
      }
    }

    const areDataChanged = computed(() => {
      const basicChanged =
        candidateDataLocal.value.firstName !== jobCandidateData.value.candidate.user.firstName ||
        candidateDataLocal.value.lastName !== jobCandidateData.value.candidate.user.lastName ||
        candidateDataLocal.value.phone !== jobCandidateData.value.candidate.user.phone ||
        candidateDataLocal.value.preferredCity !== jobCandidateData.value.preferredCity

      const candidateCustomFields = getCustomFieldsForCandidate(jobCandidateData.value.customFields || [])
      const customFieldsChanged =
        !_.isEmpty(_.differenceWith(localCustomFields.value, candidateCustomFields, _.isEqual)) ||
        !_.isEmpty(_.differenceWith(candidateCustomFields, localCustomFields.value, _.isEqual))

      return basicChanged || customFieldsChanged
    })

    const restoreDefaults = () => {
      syncLocalState()
    }

    return {
      handleFormSubmit,
      restoreDefaults,

      valid,
      loading,
      dataForm,
      candidateDataLocal,
      localCustomFields,
      areDataChanged,

      validators: {
        required,
      },
    }
  },
}
</script>
